<script setup lang="ts">
import Badge from '~/components/Common/Badge/Badge.vue'
import type { Forum, ForumPost, ForumPostVote } from '~/types/forum'
import type { AvatarProps, Profile } from '~/types/profile'
import Button from '~/components/Common/Button/Button.vue'
import Modal from '~/components/Common/Modal/Modal.vue'
import SkeletonLoader from '~/components/SkeletonLoader.vue'
import { usePostsStore } from '~/stores/forum/Posts'
import { useCommentStore } from '~/stores/forum/Comments'
import { useRouter } from '#imports'
import { ResourceType, AlertType } from '~/types/common'
import TiptapEditor from '~/components/TiptapEditor.vue'
import { toTypedSchema } from '@vee-validate/zod'
import { useField, useForm } from 'vee-validate'
import * as zod from 'zod'
import type { ToastNotification } from '~/components/Toast.vue'
import Search from '~/components/Discussions/Preview/Search.vue'
const { $goto, $navigate } = useNuxtApp()


interface Props {
	post: ForumPost
	forum: Forum
	destination?: undefined | 'post'
	avatar?: Partial<AvatarProps>
	theme?: 'full' | 'slim' | 'slim-profile' | 'search'
	showTypeTag?: boolean
	isPostLoading?: boolean
	emitResult?: boolean
	closeOnSelect?: boolean
}

const props = withDefaults(defineProps<Props>(), {
	destination: undefined,
	theme: 'full',
})

const emits = defineEmits(['selected', 'close'])

const isAuthed = inject('isAuthed') as Ref<boolean>
const profile = inject('profile') as Ref<Profile>
const toast = inject('toast') as Ref<ToastNotification>

const localPost = ref<ForumPost>(props.post)

const router = useRouter()
const postStore = usePostsStore(props.forum.id)
const commentStore = useCommentStore(props.forum.id, props.post.id)

const commentModelOpen = ref<boolean>(false)
const error = ref<string | null>(null)
const isLoading = ref(false)

// Share URLs
const url = useRequestURL()
const pageUrl = `${url.origin}/discussions/${props.forum.handle}/${props.post.handle}`
const pageTitle = `${props.post.title} - ${props.forum.name} - Discussion - Health Nutrition`

function copyLink() {
	navigator.clipboard.writeText(pageUrl)

	toast.value = {
		show: true,
		timeout: 3,
		title: 'Link Copied',
		message: 'Discussion link has been copied to clipboard',
		type: 'success',
	}
}

const reportDiscussionModelOpen = ref<boolean>(false)

function report() {
	reportDiscussionModelOpen.value = true
}

async function confirmReport() {
	reportDiscussionModelOpen.value = false
	toast.value = {
		show: true,
		timeout: 10,
		title: `Discussion reported`,
		message: `You have successfully reported this discussion.`,
		type: 'success',
	}
}

const areYouSureDeletePost = ref<boolean>(false)
const areYouSureDeletePostId = ref<number | null>(null)
const upvoted = computed(
	() => !!localPost.value?.forums_posts_upvotes?.find((v: ForumPostVote) => v.user_id === profile?.value?.id),
)
const downvoted = computed(
	() => !!localPost.value?.forums_posts_downvotes?.find((v: ForumPostVote) => v.user_id === profile?.value?.id),
)

const validationSchema = toTypedSchema(
	zod.object({
		content: zod.string().min(10, { message: 'Too short, add more detail to your comment.' }),
	}),
)

const { handleSubmit, errors } = useForm({
	validationSchema,
})

const { value: content } = useField('content', { value: '' })

function contentUpdated(value: string) {
	content.value = value
}

const submit = handleSubmit(async values => {
	error.value = null
	isLoading.value = true

	try {
		const comment = {
			content: values.content,
			post_id: props.post.id,
			user_id: profile.value?.id,
		}

		const response = await commentStore().create(comment)

		if (!response.id) {
			toast.value = {
				show: true,
				timeout: 10,
				title: `Error posting comment`,
				message: `Error creating discussion. Please try again.`,
				type: 'error',
			}
		} else {
			commentModelOpen.value = false
			toast.value = {
				show: true,
				timeout: 10,
				title: `Comment posted`,
				message: `Your comment has been posted successfully.`,
				type: 'success',
			}
			content.value = ''

			$goto(`/discussions/${props.forum.handle}/${props.post.handle}#comment-${response.id}`)
		}
	} catch (err: any) {
		console.error(err)
		toast.value = {
			show: true,
			timeout: 10,
			title: `Error posting comment`,
			message: `Error creating discussion. Please try again.`,
			type: 'error',
		}
	} finally {
		isLoading.value = false
	}
})

async function comment() {
	if (!isAuthed.value) {
		const title = `Login To Comment`
		const message = `Please login (or register for free) to comment on this discussion. Once completed, you will be redirected back to this discussion.`
		$navigate(`/login?redirect=${router.currentRoute.value.fullPath}&toast_title=${encodeURI(title)}&toast_message=${encodeURI(message)}`)
	} else {
		commentModelOpen.value = true
	}
}

async function confirmDeletePost() {
	try {
		await postStore().delete(areYouSureDeletePostId.value)
		areYouSureDeletePostId.value = null
		$navigate(`/discussions/${props.forum.handle}`)
	} catch (e) {
		console.error(e)
		toast.value = {
			show: true,
			timeout: 10,
			title: `Error deleting discussion`,
			message: `There was an error discussion your comment, please try again later.`,
			type: 'error',
		}
	}
}

async function upvote() {
	if (!isAuthed.value) {
		const title = `Login To Upvote`
		const message = `Please login (or register for free) to upvote this discussion. Once completed, you will be redirected back to this discussion.`
		$navigate(`/login?redirect=${router.currentRoute.value.fullPath}&toast_title=${encodeURI(title)}&toast_message=${encodeURI(message)}`)
	} else {
		try{
			await postStore().upvote({
				post_id: props.post.id,
				user_id: profile?.value?.id,
			})
			localPost.value = await postStore().get(props.post.id)
			toast.value = {
				show: true,
				timeout: 10,
				title: 'Discussion upvoted',
				message: 'You have successfully upvoted this discussion.',
				type: 'success',
			}
		} catch (error) {
			console.error('Error upvoting discussion:', error)
			toast.value = {
				show: true,
				timeout: 10,
				title: 'Error upvoting discussion',
				message: 'There was an error upvoting this discussion. Please try again.',
				type: 'error',
			}
		}
	}
}

async function removeUpvote() {
	if (!isAuthed.value) {
		const title = `Login To Remove Upvote`
		const message = `Please login (or register for free) to remove your upvote from this discussion. Once completed, you will be redirected back to this discussion.`
		$navigate(`/login?redirect=${router.currentRoute.value.fullPath}&toast_title=${encodeURI(title)}&toast_message=${encodeURI(message)}`)
	} else {
		try{
			await postStore().removeUpvote({
				post_id: props.post.id,
				user_id: profile?.value?.id,
			})
			localPost.value = await postStore().get(props.post.id)
			toast.value = {
				show: true,
				timeout: 10,
				title: 'Upvote removed',
				message: 'You have successfully removed your upvote.',
				type: 'success',
			}
		} catch (error) {
			console.error('Error removing upvote:', error)
			toast.value = {
				show: true,
				timeout: 10,
				title: 'Error removing upvote',
				message: 'There was an error removing your upvote. Please try again.',
				type: 'error',
			}
		}
	}
}

async function downvote() {
	if (!isAuthed.value) {
		const title = `Login To Downvote`
		const message = `Please login (or register for free) to downvote this discussion. Once completed, you will be redirected back to this discussion.`
		$navigate(`/login?redirect=${router.currentRoute.value.fullPath}&toast_title=${encodeURI(title)}&toast_message=${encodeURI(message)}`)
	} else {
		try{
			await postStore().downvote({
				post_id: props.post.id,
				user_id: profile?.value?.id,
			})
			localPost.value = await postStore().get(props.post.id)
			toast.value = {
				show: true,
				timeout: 10,
				title: 'Discussion downvoted',
				message: 'You have successfully downvoted this discussion.',
				type: 'success',
			}
		} catch (error) {
			console.error('Error downvoting discussion:', error)
			toast.value = {
				show: true,
				timeout: 10,
				title: 'Error downvoting discussion',
				message: 'There was an error downvoting this discussion. Please try again.',
				type: 'error',
			}
		}
	}
}

async function removeDownvote() {
	if (!isAuthed.value) {
		const title = `Login To Remove Downvote`
		const message = `Please login (or register for free) to remove your downvote from this discussion. Once completed, you will be redirected back to this discussion.`
		$navigate(`/login?redirect=${router.currentRoute.value.fullPath}&toast_title=${encodeURI(title)}&toast_message=${encodeURI(message)}`)
	} else {
		try{
			await postStore().removeDownvote({
				post_id: props.post.id,
				user_id: profile?.value?.id,
			})
			localPost.value = await postStore().get(props.post.id)
			toast.value = {
				show: true,
				timeout: 10,
				title: 'Downvote removed',
				message: 'You have successfully removed your downvote.',
				type: 'success',
			}
		} catch (error) {
			console.error('Error removing downvote:', error)
			toast.value = {
				show: true,
				timeout: 10,
				title: 'Error removing downvote',
				message: 'There was an error removing your downvote. Please try again.',
				type: 'error',
			}
		}
	}
}

async function deletePost() {
	areYouSureDeletePostId.value = props.post.id
	areYouSureDeletePost.value = true
}

function clicked(){
	if (props.emitResult) {
		emits('selected', props.post)

		if(props.closeOnSelect){
			emits('close')
		}

	} else {

		if(props.closeOnSelect){
			emits('close')
		}

		$navigate(`/discussions/${props.forum.handle}/${props.post.handle}`)
	}
}


</script>

<template>

	<Search v-if="theme === 'search'" :post="post"	:showTypeTag="showTypeTag" @selected="clicked"/>


	<article
	v-else
		:class="[
			post.users[0].id === profile?.id && theme !== 'slim-profile' ? 'bg-green-50' : 'bg-white',
			'card',
			destination && destination === 'post' ? 'cursor-pointer' : '',
		]"
		@click="clicked">
		<!-- Skeleton Loading Full -->
		<div v-if="isPostLoading && theme === 'full'" class="p-6">
			<div class="flex flex-col gap-4">
				<div class="flex flex-row items-center gap-2">
					<SkeletonLoader class="h-12 w-12 rounded-full" />
					<div class="flex flex-col gap-1">
						<SkeletonLoader class="h-4 w-24" />
						<SkeletonLoader class="h-3 w-16" />
					</div>
				</div>
				<SkeletonLoader class="h-8 w-3/4" />
				<SkeletonLoader class="h-32 w-full" />
			</div>
		</div>
		<!-- FULL -->
		<div v-else-if="theme === 'full'" class="p-2 sm:py-2 sm:px-4">
			<div class="flex flex-col gap-2 pb-2">
				<div class="flex flex-row border-b border-gray-100 pb-2">
					<div class="flex flex-row items-center gap-2">
						<Avatar
							:is-authed="isAuthed"
							:self="profile"
							:profile="post.users?.[0] as Profile"
							:size="12"
							type="rounded"
							text-size="text-sm"
							:hide-popover="avatar?.hidePopover ?? false" />
						<div>
							<h4 class="text-base text-gray-500 mb-0">{{ post.users?.[0].username }}</h4>
							<p class="text-xs text-gray-400">{{ ago(post.created_at) }}</p>
						</div>
					</div>
				</div>
				<h1 class="pt-2 text-lg sm:text-xl font-bold text-green-500">
					{{ post.title }}
				</h1>

				<span class="p" v-html="post.content" />

				<Button class="mb-2 max-w-64" as="div" @click="comment">Reply</Button>
			</div>

			<div class="flex flex-row items-center border-t border-gray-100">
				<div class="flex flex-row items-center gap-4 flex-1">
					<div v-if="post.users[0].id !== profile?.id" class="flex flex-row gap-1">
						<!-- Upvote -->
						<NuxtLink
							v-if="!upvoted && !downvoted"
							class="flex items-center justify-center font-semibold text-gray-900"
							@click="upvote">
							<Icon
								name="ph:thumbs-up-duotone"
								class="size-5 flex-none text-gray-500 hover:text-green-500"
								aria-hidden="true" />
						</NuxtLink>
						<NuxtLink
							v-else-if="upvoted && !downvoted"
							class="flex items-center justify-center font-semibold text-gray-900"
							@click="removeUpvote">
							<Icon name="ph:thumbs-up-fill" class="size-5 flex-none text-gray-500 hover:text-green-500" aria-hidden="true" />
						</NuxtLink>
						<div v-else class="flex items-center justify-center font-semibold text-gray-400">
							<Icon
								name="ph:thumbs-up-duotone"
								class="size-5 flex-none text-gray-400"
								aria-hidden="true" />
						</div>

						<!-- Downvote -->
						<NuxtLink
							v-if="!upvoted && !downvoted"
							class="flex items-center justify-center font-semibold text-gray-900"
							@click="downvote">
							<Icon
								name="ph:thumbs-down-duotone"
								class="size-5 flex-none text-gray-500 hover:text-green-500"
								aria-hidden="true" />
						</NuxtLink>
						<NuxtLink
							v-else-if="!upvoted && downvoted"
							class="flex items-center justify-center font-semibold text-gray-900"
							@click="removeDownvote">
							<Icon
								name="ph:thumbs-down-fill"
								class="size-5 flex-none text-gray-500 hover:text-green-500"
								aria-hidden="true" />
						</NuxtLink>
						<div v-else class="flex items-center justify-center font-semibold text-gray-400">
							<Icon
								name="ph:thumbs-down-duotone"
								class="size-5 flex-none text-gray-400"
								aria-hidden="true" />
						</div>
						<span class="text-sm">{{
							(localPost.forums_posts_upvotes?.length ?? 0) - (localPost.forums_posts_downvotes?.length ?? 0)
						}}</span>
					</div>

					<div v-if="post.users[0].id === profile?.id" class="flex flex-row gap-1">
						<Icon name="ph:thumbs-up-duotone" class="size-5 flex-none text-gray-400" aria-hidden="true" />
						<span class="text-sm">{{ localPost.votes }}</span>
					</div>

					<!-- Comment -->
					<div class="flex items-center gap-1">
						<NuxtLink
							class="flex items-center justify-center gap-1 font-semibold text-gray-900"
							@click="comment">
							<Icon name="ph:chat-duotone" class="size-5 flex-none text-gray-500 hover:text-green-500" aria-hidden="true" />
						</NuxtLink>
						<span class="text-sm">{{ post.reply_count }}</span>
					</div>

					<Share :title="pageTitle" :url="pageUrl" @copied="copyLink" />
				</div>

				<div class="flex flex-row items-center gap-0 px-0 lg:px-2 pt-2 pb-1">
					<a
						class="text-gray-700 block text-sm no-underline"
						@click="() => copyLink()">
						<Icon name="ph:link-duotone" class="size-5 icon-fix hover:text-green-500"
					/></a>
					<a
						v-if="isAuthed && post?.users?.[0].id === profile?.id"
						class="text-gray-700 block text-sm no-underline hover:text-red-800"
						@click="() => deletePost()">
						<Icon name="ph:trash-duotone" class="size-5 icon-fix"
					/></a>
					<a
						v-if="isAuthed && post?.users?.[0].id !== profile?.id"
						class="text-gray-700 block text-sm no-underline hover:text-red-800"
						@click="() => report()">
						<Icon name="ph:warning-duotone" class="size-5 icon-fix" /><span class="hidden sm:inline"
							>Report</span
						></a
					>

					<Badge v-if="showTypeTag" color="blue" class="ml-2">DISCUSSION</Badge>
				</div>
			</div>
		</div>

		<!-- Skeleton Loading Slim -->
		<div v-else-if="isLoading && (theme === 'slim' || theme === 'slim-profile')" class="flex flex-row items-center">
			<div
				v-if="theme === 'slim'"
				class="flex flex-col basis-2/12 sm:basis-1/12 border-r border-gray-100 p-2 text-center items-center">
				<SkeletonLoader class="h-10 w-10 rounded-full" />
			</div>
			<div
				v-if="theme === 'slim-profile'"
				class="flex flex-col basis-1/12 border-r border-gray-100 p-2 text-center items-center">
				<SkeletonLoader class="h-7 w-7" />
			</div>
			<div class="flex flex-col grow basis-8/12 sm:basis-9/12 p-2 sm:px-4 gap-2">
				<SkeletonLoader class="h-5 w-3/4" />
				<SkeletonLoader class="h-3 w-1/4" />
			</div>
			<div class="flex flex-col text-center justify-center items-center basis-1/12 border-l border-gray-100 p-2">
				<SkeletonLoader class="h-5 w-5" />
				<SkeletonLoader class="h-4 w-4" />
			</div>
			<div class="flex flex-col text-center justify-center items-center basis-1/12 border-l border-gray-100 p-2">
				<SkeletonLoader class="h-5 w-5" />
				<SkeletonLoader class="h-4 w-4" />
			</div>
		</div>
		<!-- SLIM -->
		<div
			v-else-if="theme === 'slim' || theme === 'slim-profile'"
			class="flex flex-row items-center hover:bg-gray-50 cursor-pointer">
			<div
				v-if="theme === 'slim'"
				class="flex flex-col basis-2/12 sm:basis-1/12 border-r border-gray-100 p-2 text-center items-center">
				<Avatar
					:is-authed="isAuthed"
					:self="profile"
					:profile="post.users[0]"
					:size="10"
					type="rounded"
					text-size="text-sm"
					:hide-popover="avatar?.hidePopover ?? false" />
			</div>

			<div
				v-if="theme === 'slim-profile'"
				class="flex flex-col basis-1/12 border-r border-gray-100 p-2 py-4 text-center items-center">
				<Icon name="ph:chats-circle-duotone" class="size-7 flex-none text-gray-400" aria-hidden="true" />
			</div>

			<div class="flex flex-col grow basis-8/12 sm:basis-9/12 p-2 sm:px-4">
				<h2 class="text-sm sm:text-base font-bold text-green-500 my-0">
					{{ post.title }}
				</h2>

				<span class="text-xs text-gray-400">{{ ago(post.created_at) }}</span>
			</div>

			<div class="flex flex-col text-center justify-center items-center basis-1/12 border-l border-gray-100 p-2">
				<Icon name="ph:thumbs-up-duotone" class="size-5 flex-none text-gray-400" aria-hidden="true" />
				<span class="text-sm">{{ post.votes }}</span>
			</div>

			<div class="flex flex-col text-center justify-center items-center basis-1/12 border-l border-gray-100 p-2">
				<Icon name="ph:chat-duotone" class="size-5 flex-none text-gray-500" aria-hidden="true" />
				<span class="text-sm text-black">{{ post.reply_count }}</span>
			</div>
		</div>
	</article>

	<Modal
		:show="commentModelOpen"
		@close="() => (commentModelOpen = false)"
		title="Reply To Discussion"
	>

	<p>Comment on the discussion: {{ post.title }}</p>

		<TiptapEditor
			id="content"
			v-model="content"
			:content="(content as string) ?? ''"
			name="content"
			class="mt-4"
			@input="contentUpdated" />

		<span class="text-red-700 text-sm">{{ errors.content }}</span>

		<div  class="pt-2">
			<Button :disabled="isLoading" @click="submit">{{
				isLoading ? 'Posting Comment...' : 'Post Comment'
			}}</Button>
		</div>

		<Alert v-if="error" :type="AlertType.Error" class="slideInUp mt-4 text-center" :html="error"></Alert>


	</Modal>





	<AreYouSure
		:show="areYouSureDeletePost"
		:confirm="{ text: 'Delete discussion' }"
		:header="''"
		:message="`Are you sure you want to delete this discussion?`"
		@confirmed="confirmDeletePost"
		@close="() => (areYouSureDeletePost = false)" />

	<Report
		:show="reportDiscussionModelOpen"
		:type="ResourceType.FORUM_POST"
		:data="{
			resource_type: ResourceType.FORUM_POST,
			resource_id: post.id,
			user_id: profile?.id,
		}"
		@complete="confirmReport"
		@close="() => (reportDiscussionModelOpen = false)" />
</template>
